/**
 * Notes captured outside the app: the Android home-screen widget and Quick
 * Settings tile, and the iOS App Intent (Shortcuts, Siri, Action button).
 *
 * Native code writes them to a store it shares with the app (SharedPreferences
 * on Android, an App Group container on iOS) through the local `CaptureInbox`
 * plugin. The app imports them into its normal offline queue, which does the
 * syncing. On the web there is nothing outside the app, so this is a no-op.
 */
import { registerPlugin } from '@capacitor/core'
import { hasPlugin, isNative } from './platform'

/** A note as native code saved it. `client_id` is fixed when it is made. */
export interface NativeNote {
  client_id: string
  /** The rider who was signed in when it was made. */
  user_id: string
  text: string
  bike_id: string | null
  /** ISO 8601 with offset. */
  captured_at: string
}

/** What native UI needs to offer a bike picker without the web app running. */
export interface NativeContext {
  /** Native UI only saves notes while a rider is signed in, and tags them with this id. */
  user_id: string
  bikes: { id: string; name: string }[]
  current_bike_id: string | null
}

interface CaptureInboxPlugin {
  list(): Promise<{ notes: NativeNote[] }>
  remove(options: { client_ids: string[] }): Promise<void>
  setContext(options: NativeContext): Promise<void>
  clearContext(): Promise<void>
}

const plugin = registerPlugin<CaptureInboxPlugin>('CaptureInbox')
const available = () => isNative() && hasPlugin('CaptureInbox')

export async function listNativeNotes(): Promise<NativeNote[]> {
  if (!available()) return []
  const { notes } = await plugin.list()
  return notes
}

export async function removeNativeNotes(clientIds: string[]): Promise<void> {
  if (!available() || !clientIds.length) return
  await plugin.remove({ client_ids: clientIds })
}

export async function setNativeContext(context: NativeContext): Promise<void> {
  if (!available()) return
  await plugin.setContext(context).catch(() => {})
}

/** On sign-out: native UI must not offer one rider's bikes to the next. */
export async function clearNativeContext(): Promise<void> {
  if (!available()) return
  await plugin.clearContext().catch(() => {})
}
